<script lang="ts" setup>
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { onMounted, ref } from "vue";
import { useAcl } from "vue-simple-acl";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";
import { useToggle } from "~/core/composables/useToggle";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import {
  PATH_SECTORS,
  PATH_SECTORS_CODE_DTWINS,
  PATH_SECTORS_CODE_GROUPS,
  PATH_SECTORS_CODE_USERS,
} from "~/modules/sector/constants";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import sectorsService from "~/modules/sector/services/sectors.service";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { findSectorParentCode } from "~/modules/sector/utils/findSectorParentCode";
import ILxcSettings from "~icons/lxc/settings";

const acl = useAcl();
const route = useRoute();
const router = useRouter();

const { t } = useI18n();
const { sectors } = storeToRefs(useSectorStore());

const { currentOrganization } = storeToRefs(useOrganizationStore());
const sectorCode: Ref<string> = ref(route.params.code.toString());

const parentSectorCode = computed(() => {
  if (sectors.value) {
    return findSectorParentCode(sectors.value, Number(sectorCode.value));
  }
});

const [formDialogVisible, toggleFormDialogVisible] = useToggle();

const isLoading = ref(false);
const sector: Ref<Sector | undefined> = ref();
const error: Ref<LxcError | null> = ref(null);

async function fetchSector() {
  isLoading.value = true;
  if (currentOrganization.value?.code) {
    const response = await sectorsService.getSectorByCode(
      currentOrganization.value?.code,
      sectorCode.value,
    );

    if (LxcError.check(response)) {
      error.value = response;
    } else {
      sector.value = response;
      error.value = null;
    }
  }

  isLoading.value = false;
}

//
// Navigation section. Logic / configuration responsible for the navigation.
//
const dtwinsTabId = "dtwins";
const usersTabId = "users";
const groupsTabId = "groups";

const tabNavigations: TabNavigation[] = [];

if (acl.can(ACLRoles.CAN_READ_DEVICE)) {
  tabNavigations.push({
    id: dtwinsTabId,
    redirectPath: router.resolve({
      name: PATH_SECTORS_CODE_DTWINS,
      params: { code: sectorCode.value },
    }).href,
    labelTranslationKey: "sectors.details.tab.dtwins.label",
    forceRefresh: true,
  });
}

if (acl.can(ACLRoles.CAN_READ_USER)) {
  tabNavigations.push(
    {
      id: usersTabId,
      redirectPath: router.resolve({
        name: PATH_SECTORS_CODE_USERS,
        params: { code: sectorCode.value },
      }).href,
      labelTranslationKey: "sectors.details.tab.user.label",
      forceRefresh: true,
    },
    {
      id: groupsTabId,
      redirectPath: router.resolve({
        name: PATH_SECTORS_CODE_GROUPS,
        params: { code: sectorCode.value },
      }).href,
      labelTranslationKey: "sectors.details.tab.userGroup.label",
      forceRefresh: true,
    },
  );
}

const onClickSettingsButton = () => {
  toggleFormDialogVisible();
};

const onCloseSectorForm = (refresh: boolean = false) => {
  toggleFormDialogVisible(false);
  if (refresh) {
    fetchSector();
  }
};

//
// Lifecycle Hooks. See: https://vuejs.org/api/composition-api-lifecycle
//
onMounted(fetchSector);
</script>

<template>
  <page-component
    :name="sector?.name ?? ''"
    should-not-translate
    :back-default-route="PATH_SECTORS"
    :back-exclude-routes="
      tabNavigations.map((tabNavigation) => tabNavigation.redirectPath)
    "
  >
    <template #subtitle>
      <lxc-button
        v-if="!isLoading"
        :title="t('sectors.details.buttons.details')"
        type="borderless"
        class="!px-0 !py-0 ml-4"
        :icon="ILxcSettings"
        icon-class="!w-6 h-6"
        @click="onClickSettingsButton"
      />
    </template>
    <template v-if="sector" #default>
      <tabs-with-navigation :tab-navigations="tabNavigations">
        <template #[dtwinsTabId]>
          <sector-detail-dtwins :sector="sector" />
        </template>
        <template #[usersTabId]>
          <sector-detail-users :sector="sector" />
        </template>
        <template #[groupsTabId]>
          <sector-detail-user-groups :sector="sector" />
        </template>
      </tabs-with-navigation>
      <sector-form
        v-model:show="formDialogVisible"
        :parent-sector-code="parentSectorCode!"
        :sector="sector"
        @close="onCloseSectorForm(false)"
        @change="onCloseSectorForm(true)"
      />
    </template>
  </page-component>
</template>
